import { CacheIndex, EnrichmentCacheEntry, PhoneLookupLocalCacheOptions } from './types';
import { maskPhone } from './normalize';

const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const DEFAULT_MAX_ENTRIES = 1000;

function safeParse<T>(value: string | null): T | undefined {
  if (!value) return undefined;
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
}

export class PhoneLookupLocalCache {
  private userId: string | null = null;
  private ttlMs: number;
  private maxEntries: number;

  constructor(options: PhoneLookupLocalCacheOptions) {
    this.ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
    this.maxEntries = options.maxEntries ?? DEFAULT_MAX_ENTRIES;
    // Don't resolve userId yet - wait until first use
  }

  private getUserId(): string {
    if (this.userId) {
      return this.userId;
    }

    // Lazy initialization - get real userId from webex
    const realUserId = (window as any)?.webex?.internal?.device?.userId;
    
    if (!realUserId) {
      throw new Error('[Enrichment] Cache: Cannot access cache - webex userId not available. Ensure webex is initialized before using enrichment cache.');
    }
    
    this.userId = realUserId;
    return realUserId;
  }

  private getIndexKey(): string {
    return `phoneEnrich:v1:${this.getUserId()}:index`;
  }

  private getEntryKey(phone: string): string {
    return `phoneEnrich:v1:${this.getUserId()}:${phone}`;
  }

  private loadIndex(): CacheIndex {
    const index = safeParse<CacheIndex>(localStorage.getItem(this.getIndexKey()));
    return index || {};
  }

  private saveIndex(index: CacheIndex): void {
    try {
      localStorage.setItem(this.getIndexKey(), JSON.stringify(index));
    } catch (error) {
      console.error('Failed to save cache index', error);
    }
  }

  private readEntry(phone: string): EnrichmentCacheEntry | undefined {
    const raw = localStorage.getItem(this.getEntryKey(phone));
    return safeParse<EnrichmentCacheEntry>(raw);
  }

  get(phone: string): EnrichmentCacheEntry | undefined {
    try {
      const entry = this.readEntry(phone);
      if (!entry) {
        return undefined;
      }

      const now = Date.now();
      const touched: EnrichmentCacheEntry = { ...entry, lastAccessTs: now };
      localStorage.setItem(this.getEntryKey(phone), JSON.stringify(touched));

      const index = this.loadIndex();
      if (index[phone]) {
        index[phone].lastAccessTs = now;
        this.saveIndex(index);
      }

      return touched;
    } catch (error) {
      console.warn(`Failed to read cache entry for ${maskPhone(phone)}`, error);
      return undefined;
    }
  }

  isExpired(phone: string): boolean {
    const entry = this.readEntry(phone);
    if (!entry) {
      return true;
    }
    return Date.now() > entry.expiresAt;
  }

  set(
    phone: string,
    data: Omit<EnrichmentCacheEntry, 'expiresAt' | 'insertionTs' | 'lastAccessTs'>
  ): boolean {
    const key = this.getEntryKey(phone);
    const now = Date.now();
    const existing = this.readEntry(phone);

    if (
      existing &&
      existing.displayName === data.displayName &&
      existing.entityId === data.entityId &&
      existing.provider === data.provider
    ) {
      // Touch timestamps for LRU/TTL refresh
      const touched: EnrichmentCacheEntry = {
        ...existing,
        lastAccessTs: now,
        expiresAt: now + this.ttlMs,
      };
      localStorage.setItem(key, JSON.stringify(touched));
      const index = this.loadIndex();
      index[phone] = {
        insertionTs: touched.insertionTs,
        lastAccessTs: touched.lastAccessTs,
      };
      this.saveIndex(index);
      return false;
    }

    const entry: EnrichmentCacheEntry = {
      ...data,
      expiresAt: now + this.ttlMs,
      insertionTs: existing?.insertionTs ?? now,
      lastAccessTs: now,
    };

    try {
      localStorage.setItem(key, JSON.stringify(entry));
      const index = this.loadIndex();
      index[phone] = {
        insertionTs: entry.insertionTs,
        lastAccessTs: entry.lastAccessTs,
      };
      this.saveIndex(index);
      this.enforceLimit();
      return true;
    } catch (error) {
      console.error(`Failed to write cache entry for ${maskPhone(phone)}`, error);
      return false;
    }
  }

  invalidate(phone: string): void {
    try {
      localStorage.removeItem(this.getEntryKey(phone));
      const index = this.loadIndex();
      delete index[phone];
      this.saveIndex(index);
    } catch (error) {
      console.warn(`Failed to invalidate cache for ${maskPhone(phone)}`, error);
    }
  }

  clear(): void {
    try {
      const index = this.loadIndex();
      for (const phone of Object.keys(index)) {
        localStorage.removeItem(this.getEntryKey(phone));
      }
      localStorage.removeItem(this.getIndexKey());
    } catch (error) {
      console.error('Failed to clear cache', error);
    }
  }

  size(): number {
    return Object.keys(this.loadIndex()).length;
  }

  listPhones(): string[] {
    return Object.keys(this.loadIndex());
  }

  /**
   * Clear only entries from contacts (preserve DSS results)
   * Called when contacts are refreshed to pick up new contact data
   */
  clearContactsEntries(): number {
    let cleared = 0;
    try {
      const index = this.loadIndex();
      const phones = Object.keys(index);
      
      for (const phone of phones) {
        const entry = this.readEntry(phone);
        if (entry?.provider === 'webex-contacts') {
          this.invalidate(phone);
          cleared++;
        }
      }
    } catch (error) {
      console.error('[Enrichment] Cache: Failed to clear contacts entries', error);
    }
    return cleared;
  }

  private enforceLimit(): void {
    const index = this.loadIndex();
    const phones = Object.keys(index);

    if (phones.length <= this.maxEntries) {
      return;
    }

    const now = Date.now();
    const expired = phones.filter((phone) => {
      const entry = this.readEntry(phone);
      return entry ? entry.expiresAt < now : true;
    });

    for (const phone of expired) {
      this.invalidate(phone);
    }

    const updatedIndex = this.loadIndex();
    const remaining = Object.keys(updatedIndex);
    if (remaining.length <= this.maxEntries) {
      return;
    }

    const sortedByAccess = remaining
      .map((phone) => ({ phone, lastAccessTs: updatedIndex[phone].lastAccessTs }))
      .sort((a, b) => a.lastAccessTs - b.lastAccessTs);

    const toEvict = sortedByAccess.slice(0, remaining.length - this.maxEntries);
    for (const { phone } of toEvict) {
      this.invalidate(phone);
    }
  }
}
